import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Plus, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { MoneyValue } from './MoneyValue';
import { PrintProgress } from './PrintProgress';
import { ReceiptPreview } from './ReceiptPreview';
import { usePrintBatch, usePrintSingle } from '@/hooks/mutations';
import { ApiError } from '@/types/api';
import { getSaleDetail } from '@/api/sale.api';
import { useDeviceStore } from '@/stores/device.store';
import { useCartStore } from '@/stores/cart.store';
import { useHotkeys } from '@/hooks/useHotkeys';
import { printReceipt } from '@/lib/printReceipt';
import { printRefusalMessage } from '@/lib/printRefusalErrors';
import type { PrintTicketStatus } from '@/api/qrCode.api';
import type { CreateSaleResult } from '@/types/sale';

interface PaidScreenProps {
  result: CreateSaleResult;
}

/**
 * What a single-ticket (re)print error leaves on that ticket's row. Three
 * outcomes, and the first two are NOT printer problems:
 *
 *  - a refusal (#1746 — cancelled/refunded, or another visit day): REFUSED
 *    with the reason, and no retry button. Retrying cannot help.
 *  - ALREADY_PRINTED: the first attempt timed out AFTER reaching the printer,
 *    so the claim stood. Check the tray.
 *  - anything else: a genuine failure, retryable.
 */
function rowForPrintError(id: string, err: Error): PrintTicketStatus {
  const refusal = printRefusalMessage(err);
  if (refusal) {
    return {
      id,
      status: 'REFUSED',
      message: refusal,
      errorCode: err instanceof ApiError ? err.errorCode : null,
    };
  }
  if (err instanceof ApiError && err.errorCode === 'ALREADY_PRINTED') {
    return {
      id,
      status: 'ALREADY_PRINTED',
      message:
        'Mungkin sudah tercetak — cek printer. Jika tidak keluar, cetak ulang dari menu Pesanan.',
      errorCode: 'ALREADY_PRINTED',
    };
  }
  return {
    id,
    status: 'FAILED',
    message: err.message,
    errorCode: err instanceof ApiError ? err.errorCode : null,
  };
}

/** Success + print flow. The sale already succeeded — never block on print. */
export function PaidScreen({ result }: PaidScreenProps) {
  const navigate = useNavigate();
  const printerBound = useDeviceStore((s) => Boolean(s.ticketPrinterId));
  const clearCart = useCartStore((s) => s.clearCart);

  const { mutate: printBatch, isPending: printing } = usePrintBatch();
  // Retrying a FAILED ticket is still its FIRST print — the server released
  // the claim when the printer refused it. A reprint of a printed wristband
  // (new QR, reason) lives in Pesanan / Cetak Tiket.
  const { mutate: retryOne } = usePrintSingle();

  const [tickets, setTickets] = useState<PrintTicketStatus[]>([]);
  // A REJECTED batch produced nothing at all: onSuccess never fired, the
  // ticket list stayed empty and the panel showed a bare heading. The cashier
  // was left with no tickets, no error and no way to retry.
  const [batchError, setBatchError] = useState<string | null>(null);
  const started = useRef(false);
  const receiptPrinted = useRef(false);
  // The cashier must hand the guest a second receipt copy before moving on:
  // "Transaksi Baru" (and F9) stay locked until "Cetak Struk" is pressed once.
  const [receiptReprinted, setReceiptReprinted] = useState(false);

  // Sale detail for the receipt — a QUERY, not a one-shot mutation. Right after
  // a sale the detail can briefly race (SaleItems/QRs still committing), and a
  // mutation fired once in an effect (StrictMode double-invoke + the started
  // guard) could drop its result on the live instance. The query is
  // StrictMode-safe and retries the fresh-sale race until the receipt is ready.
  const { data: detail = null } = useQuery({
    queryKey: ['saleDetail', result.invoiceId],
    queryFn: () => getSaleDetail(result.invoiceId),
    retry: 4,
    retryDelay: 600,
    staleTime: 60_000,
  });

  const doPrint = () => {
    if (!printerBound) return;
    setBatchError(null);
    printBatch(result.invoiceId, {
      onSuccess: (res) => setTickets(res.tickets ?? []),
      onError: (err) => setBatchError(err instanceof Error ? err.message : String(err)),
    });
  };

  // Fire the batch print AFTER this effect pass survives.
  //
  // Firing `mutate()` synchronously here ORPHANED the print: React 18 runs a
  // mount effect, tears it down, and runs it again, so the mutation was issued
  // by the observer of the discarded pass. That observer is gone by the time
  // the printer answers ~15s later, so the per-call `onSuccess`/`onError` never
  // ran — react-query's cache held `status: "success"` with the FAILED tickets
  // while this screen sat on `isPrinting` for ever, showing a spinner and an
  // empty list. The cashier was never told that 0 of N tickets came out.
  //
  // Deferring by a tick lets the discarded pass cancel its own call in cleanup,
  // so the mutation is issued exactly once, by the observer that stays mounted.
  // The ref still guards against any later re-run, and it is only set once the
  // call actually goes out. This also covers the production case the same
  // hazard produces: a remount between firing and the printer answering.
  useEffect(() => {
    if (started.current) return;
    const t = setTimeout(() => {
      started.current = true;
      doPrint(); // tickets → the bound gate/ticket printer
    }, 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-print the thermal receipt once the sale detail is loaded — fires once.
  // Silent when Chrome runs with --kiosk-printing (see lib/printReceipt). The
  // short delay lets the hidden #receipt-print clone paint before print().
  //
  // The "fired" flag is set INSIDE the timeout for the same reason as above:
  // claiming it up front meant the discarded mount pass burned the only
  // allowance and then cancelled its own timer, so the receipt never printed
  // at all.
  useEffect(() => {
    if (!detail || receiptPrinted.current) return;
    const t = setTimeout(() => {
      receiptPrinted.current = true;
      printReceipt();
    }, 350);
    return () => clearTimeout(t);
  }, [detail]);

  const reprintReceipt = () => {
    if (!detail) return;
    printReceipt();
    setReceiptReprinted(true);
  };

  const newSale = () => {
    if (!receiptReprinted) return;
    // The post-checkout reset is not a removal — not logged as CART_CLEARED.
    clearCart({ reason: 'checkout' });
    navigate('/', { replace: true });
  };

  useHotkeys({ f9: newSale });

  return (
    <div className="mx-auto h-full max-w-4xl overflow-y-auto px-6 py-8 pos-scroll">
      <div className="mb-6 flex animate-gold-pulse flex-col items-center gap-2 rounded-2xl p-4 text-center">
        <CheckCircle2 className="h-16 w-16 text-success" />
        <h1 className="font-display text-3xl font-bold text-ink-900">Pembayaran Berhasil</h1>
        <p className="tnum text-sm text-ink-400">Invoice {result.invoiceId}</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-4">
          <Card>
            <CardContent className="space-y-3 pt-5">
              {result.cash && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg bg-secondary px-4 py-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-ink-600">Tunai</p>
                    <MoneyValue value={result.cash.amountTendered} className="text-xl" />
                  </div>
                  <div className="rounded-lg bg-gold-200 px-4 py-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-ink-600">Kembalian</p>
                    <MoneyValue value={result.cash.changeDue} emphasis className="text-xl" />
                  </div>
                </div>
              )}
              {typeof detail?.totalAmount === 'number' && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold uppercase text-ink-600">Total dibayar</span>
                  <MoneyValue value={detail.totalAmount} emphasis className="text-2xl" />
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-5">
              {printerBound ? (
                <PrintProgress
                  tickets={tickets}
                  isPrinting={printing}
                  batchError={batchError}
                  onRetry={doPrint}
                  onReprintOne={(id) =>
                    retryOne(id, {
                      onSuccess: () =>
                        setTickets((prev) =>
                          prev.map((t) => (t.id === id ? { id, status: 'PRINTED' } : t)),
                        ),
                      onError: (err) =>
                        setTickets((prev) =>
                          prev.map((t) => (t.id === id ? rowForPrintError(id, err) : t)),
                        ),
                    })
                  }
                />
              ) : (
                <p className="rounded-md border-2 border-warning/30 bg-warning/5 px-4 py-3 text-sm font-medium text-warning">
                  Printer belum diatur — tiket dapat dicetak ulang nanti dari menu Pesanan.
                </p>
              )}
            </CardContent>
          </Card>

          <Button
            size="lg"
            variant="outline"
            className="h-14 w-full text-lg"
            onClick={reprintReceipt}
            disabled={!detail}
          >
            <Printer className="h-5 w-5" /> Cetak Struk
          </Button>

          <Button
            size="lg"
            variant="gold"
            className="h-16 w-full text-xl"
            onClick={newSale}
            disabled={!receiptReprinted}
            title={receiptReprinted ? undefined : 'Cetak struk terlebih dahulu'}
          >
            <Plus className="h-6 w-6" /> Transaksi Baru
            <kbd className="ml-1 rounded border border-ink-900/20 px-1.5 text-xs">F9</kbd>
          </Button>
        </div>

        <div>{detail && <ReceiptPreview detail={detail} />}</div>
      </div>

      {/* Print-only clone — the browser prints just this subtree (see the
          .printing-receipt thermal rules in index.css). Fed by the auto-print
          effect above and the reprint path. */}
      {detail && (
        <div id="receipt-print" aria-hidden="true" className="hidden">
          <ReceiptPreview detail={detail} />
        </div>
      )}
    </div>
  );
}
